import React, { useState } from 'react';
import { MdCheckCircle, MdClose, MdError } from 'react-icons/md';
import { cn } from '@/lib/utils';
import { Button } from '@/components/Button';

type MessageColor = 'success' | 'error' | 'caution' | 'info';

interface MessageProps {
  children: React.ReactNode;
  color: MessageColor;
  className?: string;
  onClose?: () => void;
  closable?: boolean;
}

const colorStyles: Record<MessageColor, { container: string; icon: string }> = {
  success: {
    container: 'border-primary bg-primary_lighten',
    icon: 'text-primary',
  },
  error: {
    container: 'border-danger bg-red-50',
    icon: 'text-danger',
  },
  caution: {
    container: 'border-yellow-600 bg-yellow-50',
    icon: 'text-yellow-600',
  },
  info: {
    container: 'border-gray-500 bg-gray-50',
    icon: 'text-gray-500',
  },
};

export default function Message({
  children,
  color,
  className,
  onClose,
  closable = false,
}: MessageProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-xs rounded-sm border border-solid px-3 py-4 text-main',
        colorStyles[color].container,
        className
      )}
    >
      {color === 'success' ? (
        <MdCheckCircle className={cn('size-icon-xs', colorStyles[color].icon)} />
      ) : (
        <MdError className={cn('size-icon-xs', colorStyles[color].icon)} />
      )}
      <div className="w-full">{children}</div>
      {closable && (
        <Button
          type="button"
          onClick={handleClose}
          color="tertiary"
          size="xs"
          iconOnly
          className="ml-2"
          aria-label="閉じる"
        >
          <MdClose />
        </Button>
      )}
    </div>
  );
}
